/**
 * ColumnNumberLabel
 *
 * Renders the column number (1–4) inside a prism face by positioning
 * the full 1000×1000 board SVG so that only this column's number is
 * visible within the face's clip region.
 *
 * Offset formula (aligns SVG coords with board coords inside the face):
 *   svgLeft = 3 - COLUMN_X_POSITIONS[columnIndex]
 *   svgTop  = -200  (face top = board y=200)
 *
 * The perspective container's overflow:hidden clips the SVG to the
 * 232×620 face bounds, so only one number is ever visible.
 */

import React from 'react';

const COLUMN_X_POSITIONS = [18, 264, 510, 756];

interface ColumnNumberLabelProps {
  columnIndex: 0 | 1 | 2 | 3;
}

const ColumnNumberLabel: React.FC<ColumnNumberLabelProps> = ({ columnIndex }) => {
  // Offset maps board coordinates to face-local coordinates.
  // viewBox "0 0 232 620" clips naturally — only the correct column's number
  // falls within this area; all others are outside and won't render.
  const tx = 3 - COLUMN_X_POSITIONS[columnIndex];
  const ty = -200;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 232 620"
      style={{
        position: 'absolute',
        left: 0,
        top: 0,
        width: 232,
        height: 620,
        pointerEvents: 'none',
      }}
    >
      <g transform={`translate(${tx} ${ty})`}>
      {/* Column number 1 - dark shadow */}
      <path
        fill="#262626"
        d="M127.23 218.28c.18-.11.38-.18.6-.21l5.83-.77c.14-.02.25.02.35.1.09.09.14.2.13.34l.27 28.19c0 .14-.05.26-.14.37-.08.11-.19.17-.33.19l-5.66.75a.432.432 0 0 1-.35-.1.418.418 0 0 1-.13-.34l-.2-21.97c-.02-.14-.08-.2-.19-.18l-2.97.56c-.3.04-.46-.09-.47-.4l-.13-4.41c0-.28.12-.47.38-.59l3.01-1.53Z"
      />
      {/* Column number 1 - cream */}
      <path
        fill="#f6e4c5"
        d="M128.2 217.03c.18-.11.38-.17.6-.2l5.83-.77c.14-.02.25.01.35.1s.14.2.13.34l.27 28.19c0 .14-.05.27-.14.37-.08.11-.19.17-.33.19l-5.66.75a.406.406 0 0 1-.48-.44l-.2-21.97c-.02-.14-.08-.2-.19-.18l-2.97.56c-.3.04-.46-.09-.47-.4l-.13-4.41c0-.28.12-.47.38-.59l3.01-1.53Z"
      />
      {/* Column number 2 - dark shadow */}
      <path
        fill="#262626"
        d="M374.14 241.57c-.05.06-.06.12-.04.17.02.05.07.07.15.06l7.96-1.05a.406.406 0 0 1 .48.44l.07 4.92c0 .14-.06.27-.14.37-.08.11-.19.17-.33.19l-15.59 2.06c-.3.04-.47-.1-.52-.43l-.05-5.09c0-.14.08-.36.21-.65l2.26-3.14c2.45-3.36 4.13-5.82 5.03-7.39.72-1.29 1.24-2.45 1.56-3.48.32-1.03.42-2 .3-2.9-.15-1.12-.65-1.63-1.49-1.51-.44.06-.79.3-1.07.73s-.4.94-.38 1.55l.03 1.79c0 .14-.05.27-.14.37-.08.11-.19.17-.33.19l-5.74.76a.4.4 0 0 1-.35-.1.418.418 0 0 1-.13-.34l-.03-2.08c.14-2.33.97-4.3 2.51-5.93 1.54-1.62 3.51-2.59 5.92-2.91 2.11-.28 3.87.12 5.29 1.2s2.27 2.66 2.55 4.73c.04.27.07.71.09 1.32 0 1.5-.21 2.91-.63 4.22-.43 1.31-1.12 2.75-2.08 4.32-.75 1.24-2.04 3.08-3.86 5.52l-1.52 2.08Z"
      />
      {/* Column number 2 - cream */}
      <path
        fill="#f6e4c5"
        d="M375.11 240.32c-.05.06-.06.12-.04.17.02.05.07.07.15.06l7.96-1.05c.14-.02.25.02.35.1.09.09.14.2.13.34l.07 4.92c0 .14-.06.26-.14.37-.08.11-.19.17-.33.19l-15.59 2.06c-.3.04-.47-.1-.52-.43l-.05-5.09c0-.14.08-.36.21-.65l2.26-3.14c2.45-3.36 4.13-5.82 5.03-7.39.72-1.29 1.24-2.45 1.56-3.48.32-1.03.42-2 .3-2.9-.15-1.12-.65-1.63-1.49-1.51-.44.06-.79.3-1.07.73s-.4.94-.38 1.55l.03 1.79c0 .14-.05.26-.14.37-.08.11-.19.17-.33.19l-5.74.76a.432.432 0 0 1-.35-.1.418.418 0 0 1-.13-.34l-.03-2.08c.14-2.33.97-4.3 2.51-5.93 1.54-1.62 3.51-2.59 5.92-2.91 2.11-.28 3.87.12 5.29 1.2s2.27 2.66 2.55 4.73c.04.27.07.71.09 1.32 0 1.5-.21 2.91-.63 4.22-.43 1.31-1.12 2.75-2.08 4.32-.75 1.24-2.04 3.08-3.86 5.52l-1.52 2.08Z"
      />
      {/* Column number 3 - dark shadow */}
      <path
        fill="#262626"
        d="M628.38 234.52c.07.52.13 1.36.17 2.52l-.06 2.72c-.08 2.1-.83 3.89-2.23 5.37s-3.24 2.37-5.51 2.67c-2.22.29-4.06-.04-5.54-1-1.48-.96-2.34-2.37-2.59-4.23-.05-.36-.07-.63-.07-.83-.03-.47-.06-1.2-.08-2.2.01-.33.17-.52.47-.56l5.62-.74c.3-.04.47.08.51.35.04.3.06.6.06.89 0 .29.01.56.04.81.07.55.24.96.5 1.23s.59.38 1 .33c.44-.06.79-.27 1.07-.62.27-.36.44-.82.51-1.38l.02-2.72-.04-.91c0-.67-.05-1.32-.13-1.94-.06-.47-.23-.82-.5-1.06-.27-.24-.59-.34-.98-.29-.44.06-.89.34-1.35.85-.15.13-.27.2-.35.21-.05 0-.17-.03-.35-.12l-2.92-2.79c-.1-.13-.16-.23-.17-.31-.01-.11.04-.24.16-.4l4.64-5.92c.12-.18.09-.26-.07-.24l-6.85.91a.432.432 0 0 1-.35-.1.418.418 0 0 1-.13-.34l-.06-4.88c0-.14.05-.26.14-.37.08-.11.19-.17.33-.19l14.52-1.92c.14-.02.25.01.35.1s.14.2.13.34l.08 5.04c0 .19-.09.4-.25.62l-4.25 5.78c-.09.18-.06.26.07.24 1.19-.07 2.2.38 3.04 1.35.84.97 1.3 2.22 1.39 3.74Z"
      />
      {/* Column number 3 - cream */}
      <path
        fill="#f6e4c5"
        d="M629.35 233.27c.07.52.13 1.36.17 2.53l-.06 2.72c-.08 2.1-.83 3.89-2.23 5.37s-3.24 2.37-5.51 2.67c-2.21.29-4.06-.04-5.54-1-1.48-.96-2.34-2.37-2.58-4.23-.05-.35-.07-.63-.07-.83-.03-.47-.06-1.2-.08-2.2.01-.34.17-.52.47-.56l5.62-.74c.3-.04.47.08.51.35.04.3.06.6.06.89 0 .29.01.56.04.81.07.55.24.96.5 1.23s.59.38 1 .33c.44-.06.79-.27 1.07-.62.27-.36.44-.82.51-1.38l.02-2.71-.04-.91c0-.67-.05-1.32-.13-1.95-.06-.47-.23-.82-.49-1.06-.27-.24-.59-.34-.98-.29-.44.06-.89.34-1.35.85-.15.13-.27.2-.35.21-.05 0-.17-.03-.35-.12l-2.92-2.79c-.1-.13-.16-.23-.17-.31-.01-.11.04-.24.16-.4l4.64-5.92c.12-.18.09-.26-.07-.24l-6.85.91a.406.406 0 0 1-.48-.44l-.06-4.88c0-.14.05-.27.14-.37.08-.11.19-.17.33-.19l14.52-1.92c.14-.02.25.02.35.1.09.09.14.2.13.34l.08 5.04c0 .19-.09.4-.25.62l-4.25 5.78c-.09.18-.06.26.07.24 1.19-.07 2.2.38 3.04 1.35.84.97 1.3 2.22 1.39 3.74Z"
      />
      {/* Column number 4 - dark shadow */}
      <path
        fill="#262626"
        d="M878.27 232.82a.406.406 0 0 1 .48.44l.03 5.26c0 .14-.05.27-.14.37-.08.11-.19.17-.33.19l-1.31.17c-.14.02-.2.09-.18.23l.06 6.46c0 .14-.06.26-.14.37-.08.11-.19.17-.33.19l-5.66.75a.432.432 0 0 1-.35-.1.418.418 0 0 1-.13-.34l-.06-6.46c-.02-.14-.1-.2-.23-.18l-10.09 1.34a.432.432 0 0 1-.35-.1.406.406 0 0 1-.13-.34l-.05-4.17c.02-.28.05-.48.09-.6l4.93-17.02c.1-.29.27-.45.52-.49l6.03-.8c.16-.02.28.02.35.12.07.1.09.24.05.41l-4.25 15.51c-.06.17 0 .25.16.23l2.71-.36c.14-.02.2-.1.18-.23l-.02-3.59c0-.14.05-.27.14-.37.08-.11.19-.17.33-.19l5.66-.75a.406.406 0 0 1 .48.44l.02 3.59c.02.14.09.2.23.18l1.31-.17Z"
      />
      {/* Column number 4 - cream */}
      <path
        fill="#f6e4c5"
        d="M879.24 231.57c.14-.02.25.02.35.1.09.09.14.2.13.34l.03 5.26c0 .14-.05.26-.14.37-.08.11-.19.17-.33.19l-1.31.17c-.14.02-.2.1-.18.23l.06 6.46c0 .14-.06.27-.14.37-.08.11-.19.17-.33.19l-5.66.75a.406.406 0 0 1-.48-.44l-.06-6.46c-.02-.14-.1-.2-.23-.18l-10.09 1.33c-.14.02-.25-.01-.35-.1s-.14-.2-.13-.34l-.05-4.17c.02-.28.05-.48.09-.6l4.93-17.02c.1-.29.27-.45.52-.49l6.03-.8c.16-.02.28.02.35.12.07.1.09.24.05.41l-4.25 15.51c-.06.17 0 .25.16.23l2.71-.36c.14-.02.2-.1.18-.23l-.02-3.59c0-.14.05-.26.14-.37.08-.11.19-.17.33-.19l5.66-.75c.14-.02.25.02.35.1.09.09.14.2.13.34l.02 3.59c.02.14.09.2.23.18l1.31-.17Z"
      />
      </g>
    </svg>
  );
};

export default ColumnNumberLabel;
